/* eslint-disable react/no-unknown-property */
"use client"
import Pagination from "@/components/Pagination"
import { BlogType, CategoryType, PaginationType } from "@/libs/types"
import clsx from "clsx"
import { useSearchParams } from "next/navigation"
import React from "react"
import { Container, Row } from "react-bootstrap"
import CardOne from "./CardOne"
import Categories from "./Categories"
import NoRecord from "./NoRecord"
import Search from "./Search"

interface BlogListProps {
    locale: string
    blogs: Array<BlogType>
    categories: Array<CategoryType>
    activeCate?: CategoryType
}

const INIT_PAGE_SIZE = 9

const BlogList: React.FunctionComponent<BlogListProps> = ({
    blogs = [],
    categories = [],
    activeCate,
    locale
}) => {
    const searchParams = useSearchParams()

    const findBlogWithSearch = React.useCallback(() => {
        const query = searchParams.get('search')?.toLowerCase()
        let results: BlogType[] = blogs.sort((prev: BlogType, next: BlogType) => next?.id - prev?.id)
        if (!!query) {
            results = results.filter(({ attributes: { slug, title } }: BlogType) => {
                return slug.match(query) || title.match(query)
            })
        }
        return results

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blogs, searchParams.get('search')])

    const [searchBlogs, setSearchBlogs] = React.useState<BlogType[]>(findBlogWithSearch())
    React.useEffect(() => {
        return setSearchBlogs(findBlogWithSearch())
    }, [findBlogWithSearch])

    const findPageCount = (num: number, divide: number) => {
        if (num <= divide) return 1
        let count: number = Math.round(num / divide)
        if ((num / divide) > count) {
            count += 1
        }
        return count || 1
    }

    const [page, setPage] = React.useState<PaginationType>({
        page: !!searchParams.get('page') ? parseInt(searchParams.get('page') || '1') : 1,
        pageSize: INIT_PAGE_SIZE,
        pageCount: findPageCount(searchBlogs?.length, INIT_PAGE_SIZE),
        total: searchBlogs?.length
    })

    React.useEffect(() => {
        return setPage((prev: PaginationType) => ({
            ...prev,
            page: !!searchParams.get('page') ? parseInt(searchParams.get('page') || '1') : 1,
            pageCount: findPageCount(searchBlogs?.length, INIT_PAGE_SIZE),
            total: searchBlogs?.length
        }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get('page'), searchBlogs])

    return (
        <>
            <Categories categories={categories} locale={locale} active={activeCate} />
            <Search locale={locale} />
            <div className={clsx(blogs.length !== 0 ? "py-5 bg-light block-items" : "d-none")}>
                <div className="container">
                    <div className="row">
                        {(searchBlogs && searchBlogs.length !== 0) && searchBlogs.slice((page.page - 1) * page.pageSize, page.page * page.pageSize)?.map((blog: BlogType) => {
                            return (
                                <CardOne key={blog?.id} blog={blog} locale={locale} />
                            )
                        })}
                    </div>
                </div>
                {(!!page && !!page.pageCount && page.pageCount > 1) &&
                    <div className="container">
                        <Pagination locale={locale} pagination={page} />
                    </div>}
            </div>
            {blogs.length === 0 && <NoRecord locale={locale} />}
        </>
    )
}

export default BlogList